import { captureException } from '@sentry/react';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { queryClient } from '@/clients';
import { workspaceWatchlistsQueryKeys } from '@/constants';
import { watchlistsService } from '@/services';
import {
    SubjectReference,
    SubjectWatchlist,
    Watchlist,
    WatchlistInput,
    WatchlistItem,
    WorkspacePrimitiveVisibility,
} from '@/types';
import { nanoId } from '@/utilities';

interface WatchlistStoreState {
    error?: string;
    itemsByWatchlistId: Record<string, WatchlistItem[]>;
    loadedWatchlistIds: string[];
    loadedWorkspaceId?: string;
    loading: boolean;
    loadingWorkspaceId?: string;
    loadingWatchlistIds: string[];
    watchlistsLoaded: boolean;
    watchlistErrorsById: Record<string, string | undefined>;
    watchlists: Watchlist[];
}

interface WatchlistStoreActions {
    createWatchlist: (input: WatchlistInput, workspaceId?: string) => Promise<Watchlist | undefined>;
    deleteWatchlist: (watchlistId: string) => Promise<void>;
    fetchWatchlist: (watchlistId: string) => Promise<void>;
    fetchWatchlists: (workspaceId?: string) => Promise<void>;
    hydrateWatchlists: (watchlists: Watchlist[], workspaceId?: string) => void;
    toggleSubject: (
        watchlistId: string,
        subject: SubjectReference,
        selected: boolean,
        itemId?: string,
    ) => Promise<void>;
    updateWatchlist: (watchlistId: string, input: Partial<WatchlistInput>) => Promise<Watchlist>;
}

const cloneItems = (items: Record<string, WatchlistItem[]>) => structuredClone(items);
const cloneWatchlists = (watchlists: Watchlist[]) => structuredClone(watchlists);
let watchlistsFetchSequence = 0;

const syncWatchlistCollectionCaches = (watchlists: Watchlist[], workspaceId?: string) => {
    queryClient.setQueryData(workspaceWatchlistsQueryKeys.list(workspaceId), watchlists);
    queryClient.setQueriesData<SubjectWatchlist[]>(
        { queryKey: workspaceWatchlistsQueryKeys.subject(workspaceId) },
        (subjectWatchlists) =>
            subjectWatchlists
                ? watchlists.map((watchlist) => {
                      const existing = subjectWatchlists.find((candidate) => candidate.id === watchlist.id);

                      return {
                          ...watchlist,
                          itemId: existing?.itemId,
                          selected: existing?.selected ?? false,
                      };
                  })
                : subjectWatchlists,
    );
};

export const useWatchlistStore = create<WatchlistStoreState & WatchlistStoreActions>()(
    immer((set, get) => ({
        itemsByWatchlistId: {},
        loadedWatchlistIds: [],
        loading: false,
        loadingWatchlistIds: [],
        watchlistsLoaded: false,
        watchlistErrorsById: {},
        watchlists: [],
        hydrateWatchlists: (watchlists, workspaceId) => {
            const workspaceChanged = get().watchlistsLoaded && get().loadedWorkspaceId !== workspaceId;

            set({
                ...(workspaceChanged
                    ? {
                          itemsByWatchlistId: {},
                          loadedWatchlistIds: [],
                          loadingWatchlistIds: [],
                          watchlistErrorsById: {},
                      }
                    : {}),
                loadedWorkspaceId: workspaceId,
                watchlists,
                watchlistsLoaded: true,
            });
        },
        fetchWatchlists: async (workspaceId) => {
            const currentState = get();

            if (currentState.watchlistsLoaded && currentState.loadedWorkspaceId === workspaceId) {
                return;
            }

            if (currentState.loading && currentState.loadingWorkspaceId === workspaceId) {
                return;
            }

            const requestSequence = ++watchlistsFetchSequence;
            const workspaceChanged = currentState.watchlistsLoaded && currentState.loadedWorkspaceId !== workspaceId;

            set({
                ...(workspaceChanged
                    ? {
                          itemsByWatchlistId: {},
                          loadedWatchlistIds: [],
                          loadingWatchlistIds: [],
                          watchlistErrorsById: {},
                          watchlists: [],
                          watchlistsLoaded: false,
                      }
                    : {}),
                error: undefined,
                loading: true,
                loadingWorkspaceId: workspaceId,
            });

            try {
                const watchlists = await watchlistsService.list();

                if (requestSequence !== watchlistsFetchSequence) {
                    return;
                }

                const pendingWatchlists = get().watchlists.filter((watchlist) => watchlist.id.startsWith('temp-'));
                const hydratedWatchlists = [...pendingWatchlists, ...watchlists];

                set({
                    loadedWorkspaceId: workspaceId,
                    loading: false,
                    loadingWorkspaceId: undefined,
                    watchlists: hydratedWatchlists,
                    watchlistsLoaded: true,
                });
                syncWatchlistCollectionCaches(hydratedWatchlists, workspaceId);
            } catch (error) {
                captureException(error);
                if (requestSequence === watchlistsFetchSequence) {
                    set({ error: 'Failed to load watchlists', loading: false, loadingWorkspaceId: undefined });
                }
            }
        },
        fetchWatchlist: async (watchlistId) => {
            const workspaceId = get().loadedWorkspaceId;

            if (get().loadingWatchlistIds.includes(watchlistId)) {
                return;
            }

            set((state) => {
                state.loadingWatchlistIds.push(watchlistId);
                state.error = undefined;
                delete state.watchlistErrorsById[watchlistId];
            });

            try {
                const watchlist = await watchlistsService.get(watchlistId);

                if (get().loadedWorkspaceId !== workspaceId) {
                    return;
                }

                set((state) => {
                    const watchlistSummary: Watchlist = {
                        archived: watchlist.archived,
                        canEdit: watchlist.canEdit,
                        createdAt: watchlist.createdAt,
                        description: watchlist.description,
                        favorited: watchlist.favorited,
                        id: watchlist.id,
                        itemCount: watchlist.itemCount,
                        locked: watchlist.locked,
                        teamId: watchlist.teamId,
                        title: watchlist.title,
                        updatedAt: watchlist.updatedAt,
                        visibility: watchlist.visibility,
                    };

                    state.itemsByWatchlistId[watchlistId] = watchlist.items;
                    state.watchlists = state.watchlists.map((item) =>
                        item.id === watchlistId ? { ...item, ...watchlistSummary } : item,
                    );
                    if (!state.loadedWatchlistIds.includes(watchlistId)) {
                        state.loadedWatchlistIds.push(watchlistId);
                    }
                    state.loadingWatchlistIds = state.loadingWatchlistIds.filter((id) => id !== watchlistId);
                    delete state.watchlistErrorsById[watchlistId];
                });
                syncWatchlistCollectionCaches(get().watchlists, workspaceId);
            } catch (error) {
                captureException(error);
                if (get().loadedWorkspaceId !== workspaceId) {
                    return;
                }

                set((state) => {
                    state.error = 'Failed to load this watchlist';
                    state.loadingWatchlistIds = state.loadingWatchlistIds.filter((id) => id !== watchlistId);
                    state.watchlistErrorsById[watchlistId] = 'Unable to load resources';
                });
            }
        },
        createWatchlist: async (input, workspaceId) => {
            const resolvedWorkspaceId = workspaceId ?? get().loadedWorkspaceId;
            const previousWatchlists = cloneWatchlists(get().watchlists);
            const creationWorkspaceIsCurrent = () => {
                const currentState = get();

                return (
                    currentState.loadedWorkspaceId === resolvedWorkspaceId ||
                    (!currentState.watchlistsLoaded &&
                        (currentState.loadingWorkspaceId === resolvedWorkspaceId ||
                            currentState.loadedWorkspaceId === undefined))
                );
            };
            const tempId = `temp-${nanoId()}`;
            const optimistic: Watchlist = {
                archived: input.archived ?? false,
                canEdit: true,
                createdAt: new Date().toISOString(),
                description: input.description,
                favorited: input.favorited ?? false,
                id: tempId,
                itemCount: 0,
                locked: input.locked ?? false,
                teamId: input.teamId,
                title: input.title,
                updatedAt: new Date().toISOString(),
                visibility: input.visibility ?? WorkspacePrimitiveVisibility.PRIVATE,
            };

            set((state) => {
                state.watchlists.unshift(optimistic);
            });
            syncWatchlistCollectionCaches(get().watchlists, resolvedWorkspaceId);

            try {
                const watchlist = await watchlistsService.create(input);

                if (creationWorkspaceIsCurrent()) {
                    set((state) => {
                        const reconciled = state.watchlists.map((item) => (item.id === tempId ? watchlist : item));
                        const withServerRecord = reconciled.some((item) => item.id === watchlist.id)
                            ? reconciled
                            : [watchlist, ...reconciled];

                        state.watchlists = withServerRecord.filter(
                            (item, index, records) => records.findIndex((record) => record.id === item.id) === index,
                        );
                    });
                    syncWatchlistCollectionCaches(get().watchlists, resolvedWorkspaceId);
                } else {
                    syncWatchlistCollectionCaches(
                        [optimistic, ...previousWatchlists].map((item) => (item.id === tempId ? watchlist : item)),
                        resolvedWorkspaceId,
                    );
                }

                return watchlist;
            } catch (error) {
                captureException(error);
                if (creationWorkspaceIsCurrent()) {
                    set((state) => {
                        state.watchlists = state.watchlists.filter((item) => item.id !== tempId);
                        state.error = 'Failed to create this watchlist';
                    });
                }
                syncWatchlistCollectionCaches(previousWatchlists, resolvedWorkspaceId);
            }
        },
        deleteWatchlist: async (watchlistId) => {
            const workspaceId = get().loadedWorkspaceId;
            const previousWatchlists = cloneWatchlists(get().watchlists);
            const previousItems = cloneItems(get().itemsByWatchlistId);
            const previousLoadedWatchlistIds = [...get().loadedWatchlistIds];

            set((state) => {
                state.watchlists = state.watchlists.filter((watchlist) => watchlist.id !== watchlistId);
                delete state.itemsByWatchlistId[watchlistId];
                delete state.watchlistErrorsById[watchlistId];
                state.loadedWatchlistIds = state.loadedWatchlistIds.filter((id) => id !== watchlistId);
            });
            syncWatchlistCollectionCaches(get().watchlists, workspaceId);

            try {
                await watchlistsService.delete(watchlistId);
            } catch (error) {
                captureException(error);
                if (get().loadedWorkspaceId === workspaceId) {
                    set({
                        error: 'Failed to delete this watchlist',
                        itemsByWatchlistId: previousItems,
                        loadedWatchlistIds: previousLoadedWatchlistIds,
                        watchlists: previousWatchlists,
                    });
                }
                syncWatchlistCollectionCaches(previousWatchlists, workspaceId);
                throw error;
            }
        },
        toggleSubject: async (watchlistId, subject, selected, itemId) => {
            const workspaceId = get().loadedWorkspaceId;
            const previousWatchlists = cloneWatchlists(get().watchlists);
            const previousItems = cloneItems(get().itemsByWatchlistId);
            const tempId = `temp-${nanoId()}`;
            const optimisticItem: WatchlistItem = {
                createdAt: new Date().toISOString(),
                id: itemId ?? tempId,
                metadata: subject.metadata ?? {},
                subjectId: subject.subjectId,
                subjectLabel: subject.subjectLabel,
                subjectType: subject.subjectType,
                updatedAt: new Date().toISOString(),
            };

            set((state) => {
                const currentItems = state.itemsByWatchlistId[watchlistId] ?? [];
                state.itemsByWatchlistId[watchlistId] = selected
                    ? currentItems.filter(
                          (item) =>
                              item.id !== itemId &&
                              !(item.subjectId === subject.subjectId && item.subjectType === subject.subjectType),
                      )
                    : [...currentItems, optimisticItem];
                state.watchlists = state.watchlists.map((watchlist) =>
                    watchlist.id === watchlistId
                        ? {
                              ...watchlist,
                              itemCount: Math.max(0, watchlist.itemCount + (selected ? -1 : 1)),
                          }
                        : watchlist,
                );
            });
            syncWatchlistCollectionCaches(get().watchlists, workspaceId);

            try {
                const removingPendingItem = selected && itemId?.startsWith('temp-');

                if (selected && itemId && !removingPendingItem) {
                    await watchlistsService.removeItem(watchlistId, itemId);
                } else if (!selected) {
                    const created = await watchlistsService.addItem(watchlistId, subject);

                    if (get().loadedWorkspaceId === workspaceId) {
                        const pendingItemStillExists = (get().itemsByWatchlistId[watchlistId] ?? []).some(
                            (item) => item.id === tempId,
                        );

                        if (pendingItemStillExists) {
                            set((state) => {
                                state.itemsByWatchlistId[watchlistId] = (
                                    state.itemsByWatchlistId[watchlistId] ?? []
                                ).map((item) => (item.id === tempId ? { ...item, id: created.id } : item));
                            });
                        } else {
                            await watchlistsService.removeItem(watchlistId, created.id);
                        }
                    }
                }
            } catch (error) {
                captureException(error);
                if (get().loadedWorkspaceId === workspaceId) {
                    set({
                        error: 'Failed to update this watchlist',
                        itemsByWatchlistId: previousItems,
                        watchlists: previousWatchlists,
                    });
                }
                syncWatchlistCollectionCaches(previousWatchlists, workspaceId);
                throw error;
            }
        },
        updateWatchlist: async (watchlistId, input) => {
            const workspaceId = get().loadedWorkspaceId;
            const previousWatchlists = cloneWatchlists(get().watchlists);

            set((state) => {
                state.watchlists = state.watchlists.map((watchlist) =>
                    watchlist.id === watchlistId
                        ? {
                              ...watchlist,
                              ...input,
                              updatedAt: new Date().toISOString(),
                          }
                        : watchlist,
                );
            });
            const optimisticWatchlists = cloneWatchlists(get().watchlists);

            syncWatchlistCollectionCaches(optimisticWatchlists, workspaceId);

            try {
                const watchlist = await watchlistsService.update(watchlistId, input);

                if (get().loadedWorkspaceId === workspaceId) {
                    set((state) => {
                        state.watchlists = state.watchlists.map((item) => (item.id === watchlistId ? watchlist : item));
                    });
                    syncWatchlistCollectionCaches(get().watchlists, workspaceId);
                } else {
                    syncWatchlistCollectionCaches(
                        optimisticWatchlists.map((item) => (item.id === watchlistId ? watchlist : item)),
                        workspaceId,
                    );
                }

                return watchlist;
            } catch (error) {
                captureException(error);
                if (get().loadedWorkspaceId === workspaceId) {
                    set({ error: 'Failed to update this watchlist', watchlists: previousWatchlists });
                }
                syncWatchlistCollectionCaches(previousWatchlists, workspaceId);
                throw error;
            }
        },
    })),
);
